// organize-imports-ignore — import order here is load-bearing.
/* eslint-disable max-lines */
// Import polyfills FIRST before any other code
import '../polyfills';

// Side-effect import — configures the @heidi/capture facades with today's
// app implementations (logger, flags, user cache, perf, mic readiness,
// ledger policy). MUST evaluate before every engine-reachable import — in
// particular before '@/lib/utils' below — so module-evaluation-time engine
// work already sees the wired capabilities (P2c, monorepo-kit-capture).
import '@/composition/hosts/capture/capture-wiring';

// prettier-ignore
// Fix circular dependency issue by importing lib/util at top here
// ReferenceError: Cannot access 'auth' before initialization
// prettier-ignore
import '@/lib/utils';
import { isTauri } from '@/tauri/utils';
// Side-effect import — injects env/build-profile signals into the
// @heidi/runtime doors (G1/F5: packages never read process.env). Evaluates
// here, before the feature-module imports below.
import '@/composition/wiring/runtime-wiring';
// Side-effect import — injects the app logger into @heidi/plugin-core, which is
// type:bridge and so may not import @heidi/runtime itself. Must
// follow runtime-wiring: the logger it hands over is the one configureLogger
// just configured.
import '@/composition/wiring/plugin-core-wiring';
// Side-effect import — injects the app RUM reporter into the @heidi/editor destroy-safety guards.
import '@/composition/wiring/editor-wiring';
// Side-effect import — tells @heidi/answer-kit which hrefs open in place, so
// the kit's markdown anchors keep intercepting settings routes without the
// package importing the settings-modal protocol.
import '@/composition/hosts/answer-kit/answer-kit-wiring';
// Side-effect import — tells @heidi/utils whether this build may ask a
// third-party service for source favicons (F5: packages never read the build
// profile). Off on-prem, where a cited domain must not leave the appliance.
import '@/composition/wiring/favicon-wiring';
// Side-effect import — injects the scribe HTTP client, reactive auth-token
// hook, and SCC access-context into @heidi/workspace/data (Wave 2 · W).
import '@/composition/hosts/workspace/workspace-data-wiring';
// Side-effect import — injects the app's React Query client into
// @heidi/session/data (session data core, D2).
import '@/composition/hosts/session/session-data-wiring';
// Side-effect import — injects the clinical-coding axios singleton and the
// app's queryClient into @heidi/coding/data (Wave 2 · D1). Must follow the
// workspace wiring: the coding HTTP builder's SCC-JWT interceptor mints
// through the workspace seam.
import '@/composition/hosts/coding/coding-data-wiring';
import '@/composition/hosts/templates/templates-data-wiring';
// Side-effect import — injects the app's React Query client into
// @heidi/commerce/data (commerce data core, D2). Must follow the workspace
// wiring: commerce's team-plan-upgrade mutation writes the user cache through
// workspace's GET_USER contract.
import '@/composition/hosts/commerce/commerce-data-wiring';
// Boot-time commerce billing UI ports — reached by global modals and upgrade
// flows outside settings, so they cannot wait for the lazy settings host.
import '@/composition/hosts/commerce/commerce-billing-ui-wiring';
// Follows the workspace and commerce wirings: the mutations write the user
// cache through workspace's GET_USER, and the trial refresh runs commerce's invalidation.
import '@/composition/hosts/growth/growth-data-wiring';
// Ads package data seam: token, query client, and notifications feed.
import '@/composition/hosts/ads/ads-data-wiring';
// Side-effect import — injects the calls chat service's base-URL thunk, the v2
// stream headers and the SSE client into @heidi/calls/data. Must follow the
// workspace wiring: the injected stream headers read the workspace id that
// wiring populates.
import '@/composition/hosts/calls/calls-data-wiring';
// Side-effect import — injects the inbox-service client and the workspace-id
// hook into @heidi/integrations/document-inbox/data (integrations C/D). Must
// follow the workspace wiring: the injected `useWorkspaceId` reads the store
// that wiring populates.
import '@/composition/hosts/integrations/integrations-data-wiring';
// Side-effect import — injects a LOADER for the transcription-service client
// and the reactive auth-token hook into @heidi/dictate/data (dictation D1,
// the t11nClient split). Must follow the workspace wiring: the injected
// `useAuthToken` reads the store that wiring populates, and the loaded client's
// interceptor reads the workspace-id header through the workspace door.
import '@/composition/hosts/dictation/dictation-data-wiring';
// Side-effect imports — inject the Tauri IPC ports (keyboard layout,
// permissions, hotkey registration, desktop dictation bridge) into
// @heidi/dictate, plus the desktop dictation bridge port (ruling 25).
import '@/composition/hosts/dictation/dictation-analytics-wiring';
import '@/composition/hosts/dictation/dictation-bridge-wiring';
import '@/composition/hosts/dictation/dictation-hotkey-wiring';
import '@/composition/hosts/dictation/dictation-keyboard-wiring';
import '@/composition/hosts/dictation/dictation-modal-queue-wiring';
import '@/composition/hosts/dictation/dictation-permissions-wiring';
// Side-effect import — binds the app queue, promo analytics and shared
// components into @heidi/session's session-merge onboarding host.
import '@/composition/hosts/session/session-merge-onboarding-wiring';
// Side-effect import — injects the app's React Query client into
// @heidi/evidence/data (evidence/source carve). The two carved cache commands
// (`getCachedEvidenceConfig`, `invalidateEvidenceConfig`) run from module scope,
// outside React, so there is no `useQueryClient()` in reach for them.
import '@/composition/hosts/evidence/evidence-data-wiring';
// Side-effect import — injects the ml-scribe v2 origin, the client-identity
// header pair and the synchronous auth-token read into @heidi/agents/data (the
// agent-harness UI intake). The harness transport mints an absolute stream URL
// and the SSE request headers on every reconnect, so all three are getters read
// per call rather than values captured here.
import '@/composition/hosts/agents/agents-data-wiring';
// Side-effect import — binds the app inputs the Heidi II surfaces behind
// @heidi/agents share: the modal queue, the audience reads, the build profile
// and the emits that name a TrackingEventName. Members cross by reference, so
// nothing resolves here; it reaches @heidi/agents/data and never the `.` door.
import '@/composition/hosts/agents/heidi-2-ports-wiring';
// Side-effect import — wires the read-only auth identity seam. Every module
// it reaches is already evaluated above, so boot order does not move.
import '@/composition/hosts/auth/auth-identity-wiring';
// Import to initialize Tauri IPC listeners early (critical for deep linking)
import '@/tauri/ipc';

import { Toaster } from '@heidi/design-system/toast';
import { OpenFeatureProvider } from '@openfeature/react-sdk';
import { getPersistor } from '@rematch/persist';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { ThemeProvider } from 'next-themes';
import { type AppProps, type AppType } from 'next/app';
import { Provider, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/lib/integration/react';
import 'regenerator-runtime/runtime';
import 'streamdown/styles.css';

import {
  DeferredAppShellWidgets,
  DeferredChronicleWidgets,
} from '@/app-shell/deferred-global-widgets';
import { type LayoutConfiguration } from '@/app-shell/layout-configuration';
import {
  installDevToolsConsoleToggle,
  isDevToolsEnabled,
  isDevToolsEnabledOnServer,
} from '@/components/dev/dev-tools-gate';
import { useNavMenuSwitchRouterCheckpoints } from '@/components/layout/Navigation/sidebar/nav-menu-switch-tracking';
import {
  resolveAppBootstrapMode,
  usesMinimalProviderTree,
  type AppBootstrapMode,
} from '@/components/utils/app-bootstrap-mode';
import { AppHead } from '@/components/utils/app-head';
import { AuthWrapper } from '@/components/utils/auth-wrapper';
import { BodyFontRefreshMarker } from '@/components/utils/body-font-refresh-marker';
import { DictaphoneConnectedToastMount } from '@/components/utils/dictaphone-connected-toast-mount';
import { EvidenceCapabilitiesSync } from '@/components/utils/evidence-capabilities-sync';
import GlobalModals from '@/components/utils/global-modals';
import { UserThemeManager } from '@/components/utils/ThemeManager';
import { TierColorThemeManager } from '@/components/utils/TierColorThemeManager';
import {
  IS_HEIDI_VERIFY_AUTO_MODE_ENABLED,
  IS_PYNE_AVATAR,
  useIsRecordingGuardReconcilerKilled,
} from '@/features/flags';
import { ability } from '@/helpers/casl/ability';
import { AbilityContext } from '@/helpers/casl/Can';
import { getRegion } from '@/helpers/getRegion';
import { useConsultNoteLifecycleListener } from '@/hooks/desktop/useConsultNoteLifecycleListener';
import { useOfflineRecordingEvents } from '@/hooks/desktop/useOfflineRecordingEvents';
import { useOfflineSalvageReport } from '@/hooks/desktop/useOfflineSalvageReport';
import { useOfflineStorageAvailabilitySync } from '@/hooks/desktop/useOfflineStorageAvailability';
import { useRecordingLease } from '@/hooks/desktop/useRecordingLease';
import { useSelfHealingStopEvents } from '@/hooks/desktop/useSelfHealingStopEvents';
import { useExternalLaunchRoutePrefix } from '@/hooks/integrations/useExternalLaunchRoutePrefix';
import { useActiveRecordingGuardReconciler } from '@/hooks/useActiveRecordingGuardReconciler';
import { useBlockNextNavigationGlobal } from '@/hooks/useBlockNextNavigationGlobal';
import { usePatientDirectoryAnalyticsSync } from '@/hooks/usePatientDirectoryAnalyticsSync';
import { useAuthTokenCacheScheduler } from '@/lib/auth/hooks/useAuthTokenCacheScheduler';
import { useSessionInvalidationTelemetry } from '@/lib/auth/hooks/useSessionInvalidationTelemetry';
import { SessionRecordingProvider } from '@/lib/SessionRecordingProvider';
import { Scope } from '@/lists/hotkeys';
import { TooltipProvider } from '@heidi/design-system/tooltip';
// Side-effect import to initialize Amplitude SDK on module load
import { PictureInPictureProvider } from '@/components/picture-in-picture';
import { initSnowplow } from '@/lib/snowplow';
import { useSnowplowPageView } from '@/lib/snowplow/page-view-tracker';
// P2 [3/5]: side-effect import — registers analytics context getters + debug
// hook into @heidi/runtime/analytics (adapters register at their SDK sites).
import '@/lib/analytics/wiring';
import '@/providers/amplitude-provider';
import { ChronicleStateProvider } from '@/providers/chronicle-state-provider';
import { DesignSystemProvider } from '@/providers/design-system-provider';
import { DialogDrawerRegistryProvider } from '@/providers/dialog-drawer-registry-provider';
import PosthogProvider from '@/providers/posthog-provider';
import { store, type RootState } from '@/store';
import {
  allura,
  dancingScript,
  Exposure,
  fontVariableClasses,
  generalSans,
  greatVibes,
  Inter,
  notoSans,
  notoSerif,
  pacifico,
  satisfy,
  translatorTranscriptFontFamily,
  vazirmatn,
} from '@/styles/fonts';
import '@/styles/globals.css';
import { getDesktopVersion, initDesktopVersion } from '@/utils/appVersion';
import { getDesktopOsVersion, initDesktopOsInfo } from '@/utils/desktopOsInfo';
import { getHostSpecs, initHostSpecs } from '@/utils/hostSpecs';
import { markAnalyticsWiringComplete } from '@heidi/runtime/analytics';

import { RpaRecorderRootEffects } from '@/app-shell/rpa/recorder/rpa-recorder-root-effects';
import ErrorBoundary from '@/components/error-boundary';
import { AppIntlErrorShell } from '@/components/error-boundary/app-intl-error-shell';
import { TauriTitlebarHeightVar } from '@/components/layout/header/tauri-titlebar-height-var';
import { PageLayoutSlot } from '@/components/layout/page-layout-slot';
import { DesktopNavigationGuardMount } from '@/components/utils/desktop-navigation-guard-mount';
import { DesktopRecordingRecoveryMount } from '@/components/utils/desktop-recording-recovery-mount';
import { MinimalProviders } from '@/components/utils/minimal-providers';
import { SafeTeamMembershipSync } from '@/components/utils/safe-team-membership-sync';
import { TauriCloseGuard } from '@/components/utils/tauri-close-guard';
import { TauriZoomShortcuts } from '@/components/utils/tauri-zoom-shortcuts';
import { pendingTeamJoinPorts } from '@/composition/hosts/workspace/team-join/pending-team-join-ports';
import { AutoVerifyAutoEnable } from '@/composition/hosts/session/entity-verification/AutoVerifyAutoEnable';
import { AutoVerifyCrossSessionWatcher } from '@/composition/hosts/session/entity-verification/entity-verification-app-mounts';
import { themeProviderConfig } from '@/config/theme-config';
import { KindeBrowserClientProvider } from '@/context/kinde-browser-client-context';
import { WhiteLabelProvider } from '@/context/white-label-context';
import { ClientSdkSelfReportSync } from '@/features/client-sdk-status/ClientSdkSelfReportSync';
import { FEATURE_FLAG_DOMAIN } from '@/features/flags/constants';
import { OnPremFlagBundleSync } from '@/features/flags/onprem-bundle-provider';
import { OpenFeatureContextSync } from '@/features/flags/openfeature-context-sync';
import { registerFlagProviders } from '@/features/flags/register-flag-providers';
import { Migrations } from '@/features/migrations/components/migrations';
import {
  installAbandonmentReporter,
  replayAbandonedTrackings,
} from '@/features/performanceTracking';
import { InstallSourceContextSync } from '@/hooks/useInitInstallSourceContext';
import { ApplicationInstallSync } from '@/hooks/useTrackApplicationInstall';
import { initEnhance } from '@/lib/enhance';
import { AppDirectionProvider } from '@/providers/direction-provider';
import { HeidiIntlProvider } from '@/providers/intl-provider';
import { DictationProvider } from '@/tauri/dictation';
import { ExternalLinkHandlerSync } from '@/tauri/external-link-handler-sync';
import { attachTauriRustLogsToLogger } from '@/tauri/tauriRustLogs';
import { initAnalyticsDebugger } from '@/utils/analytics/debugger';
import applyGoogleTranslateDOMPatch from '@/utils/applyGoogleTranslateDOMPatch';
import { isOnPrem } from '@/utils/buildProfile';
import { initPreviewEnvironmentBanners } from '@/utils/endpoints';
import { shouldFallbackToOsLocale } from '@/utils/i18n/shouldFallbackToOsLocale';
import { initBuildBanner } from '@/utils/logBuildBanner';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { onPersistRestored, persistOptions } from '@/utils/reactQueryPersistConfig';
import { installOfflineNetworkRejectionGuard } from '@/utils/telemetry/offline-network-rejection-guard';
import { installRejectionShapeProbe } from '@/utils/telemetry/unhandled-rejection-shape-probe';
import {
  initDatadog,
  setDatadogDesktopOsVersion,
  setDatadogHostSpecs,
} from '@/utils/tracing';
import { TranslationsProvider } from '@/utils/translations';
import { captureUtmParams } from '@/utils/utmTracking';
import { datadogLogs } from '@datadog/browser-logs';
import { getCurrentWindowLabel, isAuxiliaryWindowOffRoute } from '@heidi/plugin-core';
import { TemplateQualityGateModal } from '@heidi/templates';
import { PendingTeamJoinGate } from '@heidi/workspace';
import { GeistSans } from 'geist/font/sans';
import { LazyMotion } from 'motion/react';
import type { NextComponentType } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useSyncExternalStore } from 'react';
import { HotkeysProvider } from 'react-hotkeys-hook';
// NOTE: `react-scan` is intentionally NOT statically imported — it's a dev-only
// render profiler dynamic-imported under an opt-in flag below so it's DCE'd out
// of shipped bundles.

// Extend Next.js component type to support custom properties
type CustomNextPage = NextComponentType & {
  useMinimalProviders?: boolean;
  /** Only applied when `useMinimalProviders` is also `true`. */
  showToasters?: boolean;
  layoutConfiguration?: LayoutConfiguration;
  bootstrapMode?: AppBootstrapMode;
};

type CustomAppProps = Omit<AppProps, 'Component'> & {
  Component: CustomNextPage;
};

const ReactQueryDevtools = dynamic(
  () => import('@tanstack/react-query-devtools').then((mod) => mod.ReactQueryDevtools),
  { ssr: false }
);

// ssr:false keeps TauriAppHeader's transitive imports (useIsAuthenticated →
// tauri/auth0/provider → store/authModel) out of the SSR module graph and
// prevents the circular-dep TDZ crash during `next build` page data collection.
const TauriAppHeader = dynamic(
  () => import('@/components/layout/header/tauri-app-header'),
  { ssr: false }
);

// Build-time gate for the dev-tools tree. Direct `process.env.NEXT_PUBLIC_*`
// comparison (not `getEnvironment()`) so Next.js inlines + Terser folds it,
// letting webpack DCE the conditional `require()` block below from prod
// builds. Truthy in staging/preview/dev/localhost — same as before.
const isNonProductionEnvironment = process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production';

// Dev-only modules. `typeof import(...)` is a pure type expression (erased at
// compile time); runtime values are populated by the conditional `require()`
// below and stay `undefined` in production.
let DevToolsSidebar:
  | typeof import('@/components/dev/dev-tools-sidebar').DevToolsSidebar
  | undefined;
let OverrideOpenFeatureProvider:
  | typeof import('@/features/flags-dev/openfeature-override-provider').OverrideOpenFeatureProvider
  | undefined;
let initPostHogOverrideBridge:
  | typeof import('@/features/flags-dev/posthog-bridge').initPostHogOverrideBridge
  | undefined;
let initPostHogCallTracker:
  | typeof import('@/features/flags-dev/posthog-call-tracker').initPostHogCallTracker
  | undefined;

if (isNonProductionEnvironment) {
  /* eslint-disable @typescript-eslint/no-require-imports -- Conditional `require()` inside a build-time-foldable env check is what enables webpack to strip the dev-tools tree from production bundles. Static `import` statements would defeat dead code elimination because they're hoisted unconditionally. */
  ({ DevToolsSidebar } =
    require('@/components/dev/dev-tools-sidebar') as typeof import('@/components/dev/dev-tools-sidebar'));
  ({ OverrideOpenFeatureProvider } =
    require('@/features/flags-dev/openfeature-override-provider') as typeof import('@/features/flags-dev/openfeature-override-provider'));
  ({ initPostHogOverrideBridge } =
    require('@/features/flags-dev/posthog-bridge') as typeof import('@/features/flags-dev/posthog-bridge'));
  ({ initPostHogCallTracker } =
    require('@/features/flags-dev/posthog-call-tracker') as typeof import('@/features/flags-dev/posthog-call-tracker'));
  /* eslint-enable @typescript-eslint/no-require-imports */
}

const isPyneEnabled = IS_PYNE_AVATAR();

const persistor = getPersistor(); // Import the functions you need from the SDKs you need
if (typeof window !== 'undefined') {
  // APP-7804: register the offline network-rejection guard FIRST, before the
  // Datadog / Next.js-dev-overlay `unhandledrejection` listeners install, so its
  // capture-phase `stopImmediatePropagation()` can suppress fire-and-forget
  // analytics/session-replay upload failures when the desktop is offline.
  installOfflineNetworkRejectionGuard();
  // Observation-only: suppresses nothing, so it never needs to run before
  // another listener. Installed after the guard so the rejections the guard
  // suppresses stay out of its samples. Self-skips until initDatadog() runs.
  installRejectionShapeProbe();
  installDevToolsConsoleToggle();
  initAnalyticsDebugger();
  initBuildBanner();
  initEnhance();

  // Capture UTM params early before any navigation/redirects
  captureUtmParams();

  // APP-6397: Datadog RUM/Logs init stays SYNCHRONOUS. RUM/Logs install their
  // window.onerror / unhandledrejection / console-error instrumentation at
  // init() — and unlike PerformanceEntries there is NO browser-side buffer for
  // exceptions thrown before init. Deferring it would blind error/crash
  // telemetry for the whole boot→interactive window, which on the slow desktop
  // webview is exactly where init failures and crashes cluster (review:
  // davejcampbell, hubert on PR #12610). The heavier, buffer-safe SDKs
  // (Amplitude, Segment, PostHog surveys, Firebase) are the ones deferred.
  initDatadog();
  // Survivorship bias: replay flows a prior session left abandoned on hard
  // refresh / tab close, then arm the page-exit snapshot for this session.
  // Kept synchronous too: the reporter arms a `pagehide` listener that must be
  // live to catch a fast-bounce exit before idle, and it emits RUM actions so
  // it must run after the (now synchronous) initDatadog above.
  replayAbandonedTrackings();
  installAbandonmentReporter();

  // Initialize Snowplow analytics (app-lifetime singleton). Deliberately NOT
  // deferred (APP-6397): Snowplow is the primary analytics destination, has no
  // pre-init buffer (events tracked before the tracker exists are silently
  // dropped — the first page_view fires on mount), and on-prem it is the only
  // permitted emitter (same-origin collector contract).
  initSnowplow();

  // P2 [3/5]: seal the @heidi/runtime/analytics wiring. Every adapter
  // registration happens at module scope in the SDK-owning files (all imported
  // above via the provider tree / init calls), so by this point the final
  // adapter set is registered; this flushes the pre-wiring buffer and disables
  // buffering. On-prem, no SaaS adapters registered ⇒ buffered events drop by
  // design (Snowplow's same-origin adapter is the only emitter).
  markAnalyticsWiringComplete();

  // Cloud flag-provider registration (Flagsmith), extracted to
  // `registerFlagProviders` (P3 F3) so its ON-PREM-GATE absence
  // contract is acceptance-testable. Synchronous by design — see the module
  // doc block. `OverrideOpenFeatureProvider` is only assigned inside the
  // `isNonProductionEnvironment` require above; `isDevToolsEnabled()` also
  // keeps it off on-prem until the console opt-in.
  registerFlagProviders({
    overrideOpenFeatureProvider: isDevToolsEnabled()
      ? OverrideOpenFeatureProvider
      : undefined,
    ofrepEnabled: process.env.NEXT_PUBLIC_OFREP_ENABLED === 'true',
  });

  if (isDevToolsEnabled()) {
    // Push dev-tools overrides into PostHog via its official
    // overrideFeatureFlags API. Must run before `posthog.onFeatureFlags`
    // fires — otherwise boot-time consumers (e.g. ThemeManager) read the
    // raw flag once and never re-evaluate, defeating the override.
    try {
      initPostHogOverrideBridge?.();
    } catch (err) {
      logger.warn('[flags-dev] failed to init PostHog override bridge', {
        error: String(err),
      });
    }

    // Wrap `isFeatureEnabled`/`getFeatureFlag` so the dev panel can show
    // override rows for keys PostHog hasn't evaluated for this user
    // (cohort/region targeting). Stacks safely on top of the
    // FORCE_ENABLE_ALL_FEATURE_FLAGS monkey-patch in flags.ts.
    try {
      initPostHogCallTracker?.();
    } catch (err) {
      logger.warn('[flags-dev] failed to init PostHog call tracker', {
        error: String(err),
      });
    }
  }

  // Attach Rust logs to the WebView console in Tauri
  if (isTauri()) {
    void attachTauriRustLogsToLogger().catch(logger.error);

    // Source of truth: build-time `NEXT_PUBLIC_DESKTOP_APP_VERSION` injected
    // from `src-tauri/tauri.conf.json` via `next.config.js`. `initDesktopVersion()`
    // is a no-op when the build-time value is present; otherwise it falls back
    // to `@tauri-apps/api/app::getVersion()` and logs a warning. Either way,
    // `getDesktopVersion()` returns the version synchronously once this resolves.
    void initDesktopVersion()
      .then(() => {
        const version = getDesktopVersion();
        if (!version) return;
        datadogLogs?.setGlobalContextProperty('desktop_version', version);
        // `app_version` (added 2026-05-11) is the cross-platform name that
        // will eventually subsume `desktop_version` once web/mobile populate
        // it. Both fields ship in parallel during the migration window.
        datadogLogs?.setGlobalContextProperty('app_version', version);
      })
      .catch(logger.error);

    // PRODA-174: cache native host OS version for Snowplow client_context, and
    // attach it as Datadog global context once the async IPC resolves (unknown
    // at initDatadog() time) so auto-collected RUM errors / native crashes are
    // filterable by host OS. setDatadogDesktopOsVersion tags both RUM and Logs
    // (like update_channel / build_id), unlike the Logs-only desktop_version
    // above. Not spread into Amplitude SDK traits (Snowplow already forwards to
    // Amplitude). Early events may emit null until IPC resolves — same boot gap
    // as version.
    void initDesktopOsInfo()
      .then(() => {
        const osVersion = getDesktopOsVersion();
        if (!osVersion) return;
        setDatadogDesktopOsVersion(osVersion);
      })
      .catch(logger.error);

    // At app start, not at login: boot crashes and early long-tasks both
    // precede login. The trade is that a session which never logs in carries
    // these under `device_id` only. Fire-and-forget — never block boot.
    void initHostSpecs()
      .then(() => {
        const hostSpecs = getHostSpecs();
        if (!hostSpecs) return;
        setDatadogHostSpecs(hostSpecs);
      })
      .catch(logger.error);

    const preventDefaultDragDrop = (e: DragEvent) => {
      e.preventDefault();
    };
    window.addEventListener('dragover', preventDefaultDragDrop, { passive: false });
    window.addEventListener('drop', preventDefaultDragDrop, { passive: false });

    // Prevent Backspace from triggering browser navigation when not in an input field
    // This fixes the issue where clicking on chat output or empty space and pressing Backspace
    // causes the page to reload (browser's default history navigation behavior)
    import('@/tauri/utils/preventBackspaceNavigation')
      .then(({ preventBackspaceNavigation }) => {
        window.addEventListener('keydown', preventBackspaceNavigation, {
          passive: false,
        });
      })
      .catch(logger.error);
  }

  // react-scan is a dev-only render profiler — it instruments EVERY React
  // render, so it must never run in a shipped build. It was previously gated on
  // `isNonProductionEnvironment`, but the shipped `Heidi(Staging)` build runs as
  // non-production, so staging/dogfood users ran with render instrumentation on
  // every render. Now it's strictly opt-in via
  // NEXT_PUBLIC_ENABLE_REACT_SCAN, and dynamic-imported: the env var is unset at
  // build time for every shipped build, so `=== 'true'` folds to `false` and
  // Terser drops both the branch and the `react-scan` chunk from the bundle.
  // Set NEXT_PUBLIC_ENABLE_REACT_SCAN=true locally to opt in.
  if (process.env.NEXT_PUBLIC_ENABLE_REACT_SCAN === 'true') {
    void import('react-scan')
      .then(({ scan }) => scan({ log: false }))
      .catch((error) => console.warn('Failed to initialize react-scan:', error));
  }

  if (isNonProductionEnvironment) {
    // Initialize MSW in development
    if (process.env.NEXT_PUBLIC_ENABLE_MSW === 'true') {
      import('../mocks/browser')
        .then(({ startMSW }) => {
          void startMSW();
        })
        .catch(console.error);
    }
  }
}

const NavigationGuard = () => {
  useBlockNextNavigationGlobal();
  // Level-triggered backstop for the guard atom's edge-triggered clears —
  // mounted with the guard so the release can never outlive the block. The
  // timer is read HERE and passed down: this module is already store-coupled,
  // and a new `@/store`/`react-redux` import in the hook itself is frozen by
  // the Rematch containment ratchet. Op-killswitch evaluated here so a fleet
  // misfire can be disabled without rollback (default OFF = reconciler on).
  const isReconcilerKilled = useIsRecordingGuardReconcilerKilled();
  // Select only the signals the reconciler needs — reading the whole
  // `timerModel` slice would re-render this global tree every second tick.
  // While the timer runs, pin seconds to 0 so the selector stays stable;
  // release logging only reads seconds in the leftover (timer-stopped) case.
  const isTimerRunning = useSelector((state: RootState) => state.timerModel.isRunning);
  const timerSeconds = useSelector((state: RootState) =>
    state.timerModel.isRunning ? 0 : state.timerModel.seconds
  );
  useActiveRecordingGuardReconciler({
    isTimerRunning,
    timerSeconds,
    enabled: !isReconcilerKilled,
  });
  return null;
};

// Inside HeidiIntlProvider so the hook can call useIntl() for toast copy.
/**
 * Drives the auth token cache from the React tree. Mounting this
 * component runs the bootstrap prewarm + window-focus refetch and
 * subscribes to the session-invalidation telemetry transitions.
 * Mount once, above `<AuthWrapper>`, so the cache populates before
 * AuthWrapper-gated children check the authenticated state.
 */
const AuthTokenCacheRootEffects = () => {
  useAuthTokenCacheScheduler();
  useSessionInvalidationTelemetry();
  return null;
};

/**
 * ARD-1955: router-phase checkpoints for the `perf.app.nav_menu_switch` flow
 * that `NavItem` opens on a sidebar click. Hosted here, not in `SideNav`: the
 * sidebar re-mounts with every page (and on mobile lives in a Sheet that
 * closes on the click), so a subscriber there is not alive for the whole
 * navigation and the flow would report a total with no phases.
 */
const NavMenuSwitchRouterCheckpointsMount = () => {
  useNavMenuSwitchRouterCheckpoints();
  return null;
};

const OfflineRecordingEventsMount = () => {
  useOfflineRecordingEvents();
  // One-shot read of whether the local offline database opened at boot. Headless
  // like the events hook itself, so it renders nothing, and it no-ops on web.
  // Unlike that hook it also self-gates on the offline-recording umbrella flag:
  // it is the one provider here that invokes a brand-new command, so the flag is
  // its remote kill switch if a webview ever runs ahead of the desktop binary.
  useOfflineStorageAvailabilitySync();
  return null;
};

/**
 * Its own mount so a future umbrella flag on `OfflineRecordingEventsMount`
 * cannot hide the salvage tally for the boots that produced it.
 */
const OfflineSalvageReportMount = () => {
  useOfflineSalvageReport();
  return null;
};

/**
 * HAR-1777: Rust `session_engine:consult_note` → FE `note_generation_*`.
 * Covers Chronicle bulk sync *and* offline recording — must not live inside
 * OfflineRecordingEventsMount (that host is a plausible future umbrella-flag
 * gate and would silently kill Chronicle tracking).
 */
const ConsultNoteLifecycleMount = () => {
  useConsultNoteLifecycleListener();
  return null;
};

/** Arms online-backup reconcile behind the self-healing kill switch (FR-035a). */
const SelfHealingStopEventsMount = () => {
  useSelfHealingStopEvents();
  return null;
};

/**
 * COLAB-422: keeps the shared-directory gate snapshot the `patient_*` event
 * hosts read, and the Amplitude workspace cohort. Inside `<AuthWrapper>` so the
 * setting query only runs for a signed-in user.
 */
const PatientDirectoryAnalyticsSyncMount = () => {
  usePatientDirectoryAnalyticsSync();
  return null;
};

/**
 * Proves the webview is alive for the active native recording epoch (FR-015).
 * Self-guarded by the same kill switch, so it is inert until the flag is on.
 */
const RecordingLeaseMount = () => {
  useRecordingLease();
  return null;
};

/**
 * Store-coupled host for `GlobalModals`: reads the running-timer signal here
 * and passes it down (the launch-state sync may not import the store — Rematch
 * ratchet). Must render inside the Redux `<Provider>`; MyApp's own body is
 * above it, so a `useSelector` there throws on every render.
 */
const GlobalModalsTimerMount = () => {
  const isTimerRunning = useSelector((state: RootState) => state.timerModel.isRunning);
  return <GlobalModals isTimerRunning={isTimerRunning} />;
};

/**
 * The timer read feeds only the on-prem launch-state sync, so the SaaS build
 * mounts `GlobalModals` without the subscription — no modal-tree re-render on
 * recording start/stop. `isOnPrem` is a build-time const, so this resolves to
 * one stable component per build (a conditional hook inside a single component
 * would be a rules-of-hooks hazard instead).
 */
const GlobalModalsMount = () =>
  isOnPrem ? <GlobalModalsTimerMount /> : <GlobalModals />;

const subscribeToNothing = () => () => {};
const getNoWindowLabel = () => undefined;

const MyApp: AppType = ({ Component, pageProps: { ...pageProps } }: CustomAppProps) => {
  const router = useRouter();
  const shouldMountDevTools = useSyncExternalStore(
    subscribeToNothing,
    isDevToolsEnabled,
    isDevToolsEnabledOnServer
  );
  useSnowplowPageView();
  // Must live above the full/minimal provider split: EHR launch pages use
  // MinimalProviders, and their post-launch navigation is exactly what this
  // guard prefixes to `/external/...`.
  useExternalLaunchRoutePrefix();
  // One host on every build. `GlobalModals` decides internally which of its two
  // trees a build gets; the page-level opt-out is the only reason to skip it.
  const shouldMountGlobalModals = !(
    Component.layoutConfiguration?.disableGlobalModals ?? false
  );
  // Honor the OS/browser language pre-auth where Next.js i18n routing can't:
  // always on the desktop app (static export, `router.locale` pinned to `en`),
  // on web routes served through Next's `default` locale branch, on the
  // specific web routes that render during the desktop auth handoff, and on
  // on-prem web, where the build strips the i18n config entirely
  // (`onprem/patch_config.cjs`) so `router.locale` is `undefined`.
  const fallbackToOsLocale = shouldFallbackToOsLocale({
    isDesktop: isTauri(),
    pathname: router.pathname,
    routerLocale: router.locale,
  });
  // const isMobile = useIsMobile();

  // Apply DOM patch for Google Translate and other third-party DOM manipulation
  // https://github.com/vercel/next.js/issues/58055
  useEffect(() => {
    applyGoogleTranslateDOMPatch();
  }, []);

  // Capture ?ml-scribe= / ?transcription= preview params before Next.js SPA
  // navigation strips them from the URL. Both helpers cache their result for
  // the page session after this first call (see `getTranscriptionPreviewParam`).
  useEffect(() => {
    initPreviewEnvironmentBanners(getRegion());
  }, []);

  const windowLabel = useSyncExternalStore(
    subscribeToNothing,
    getCurrentWindowLabel,
    getNoWindowLabel
  );
  // An auxiliary window's capability grants only its own page's IPC, so the
  // main shell mounted there throws "not allowed by ACL" on every call. This
  // covers client-side navigation only: on a document load the hydration render
  // uses the server snapshot and commits the shell once, so a window also needs
  // a Rust `on_navigation` pin.
  if (isAuxiliaryWindowOffRoute(windowLabel, router.pathname)) {
    return null;
  }

  const bootstrapMode = resolveAppBootstrapMode(Component);

  if (usesMinimalProviderTree(bootstrapMode)) {
    return (
      <MinimalProviders showToasters={Component.showToasters ?? true}>
        <PosthogProvider>
          {/* No <TauriAppHeader /> here on purpose — `useMinimalProviders`
              pages are the dictation widget, overlay window, and EHR
              integration launch handoffs. Auxiliary Tauri windows have
              their own minimal chrome (transparent / no decorations), and
              integration pages run inside an EHR iframe / browser — none
              of them should inherit the main Heidi window's title bar. */}
          <Component {...pageProps} />
        </PosthogProvider>
      </MinimalProviders>
    );
  }

  return (
    <ThemeProvider {...themeProviderConfig}>
      <PersistGate persistor={persistor}>
        <Provider store={store}>
          <OpenFeatureProvider domain={FEATURE_FLAG_DOMAIN}>
            <PosthogProvider>
              <PersistQueryClientProvider
                client={reactQueryClient}
                persistOptions={persistOptions}
                onSuccess={onPersistRestored}
              >
                <AppIntlErrorShell>
                  <HeidiIntlProvider
                    isPseudolocaleFeatureEnabled={isNonProductionEnvironment}
                    fallbackToOsLocale={fallbackToOsLocale}
                  >
                    <ErrorBoundary>
                      <KindeBrowserClientProvider>
                        {shouldMountDevTools && DevToolsSidebar && <DevToolsSidebar />}
                        {/* Desktop close guard — mounted at the app root, outside
                          AuthWrapper / shouldMountGlobalModals, so the
                          Rust close handler always has a live listener (a
                          prevented close with no handler traps the user). */}
                        {isTauri() && <TauriCloseGuard />}
                        {isTauri() && <TauriZoomShortcuts />}
                        {/* Recording UI recovery after a WebView reload (DSK-695).
                          Sits beside the close guard rather than inside it: a
                          reload lands the user on an arbitrary route, so
                          recovery must run before any recording surface renders
                          and independently of auth gating. */}
                        {isTauri() && <DesktopRecordingRecoveryMount />}
                        {isTauri() && <DesktopNavigationGuardMount />}
                        {/* Eager (unlike the dynamic TauriAppHeader) so the
                          --app-titlebar-height var is live from hydration —
                          page shells pad their top with it. */}
                        {isTauri() && <TauriTitlebarHeightVar />}
                        {isTauri() && <ConsultNoteLifecycleMount />}
                        <OfflineRecordingEventsMount />
                        <OfflineSalvageReportMount />
                        <NavMenuSwitchRouterCheckpointsMount />
                        {/* Mounted inside OpenFeatureProvider so the flag resolves. */}
                        <BodyFontRefreshMarker />
                        {/* Auth token cache scheduler + telemetry
                          subscriber. Mounted above <AuthWrapper> so
                          the bootstrap prewarm runs before
                          AuthWrapper-gated children check the
                          authenticated state. The Kinde-desktop
                          bridge component mounts separately inside
                          <TauriAuth>, where <KindeProvider> is
                          actually mounted — without that context,
                          useKindeAuth() would read default values
                          instead of the live SDK state. */}
                        <AuthTokenCacheRootEffects />
                        <AppDirectionProvider>
                          <DictationProvider>
                            <TooltipProvider delayDuration={350}>
                              <DesignSystemProvider>
                                <DialogDrawerRegistryProvider>
                                  <HotkeysProvider initiallyActiveScopes={[Scope.All]}>
                                    <SessionRecordingProvider>
                                      <LazyMotion
                                        strict
                                        features={() =>
                                          import('@/lib/motionFeatures').then(
                                            (mod) => mod.features
                                          )
                                        }
                                      >
                                        <AppHead />
                                        <main className={fontVariableClasses}>
                                          <style jsx global>{`
                                            :root {
                                              /* Legacy core — still referenced, not yet retired. */
                                              --font-geist-sans: ${GeistSans.style
                                                ?.fontFamily};
                                              /* Current core — Exposure headings; Inter body (General Sans when release-body-font-refresh is on). */
                                              --font-exposure: ${Exposure.style
                                                ?.fontFamily};
                                              --font-inter: ${Inter.style?.fontFamily};
                                              --font-general-sans: ${generalSans.style
                                                ?.fontFamily};
                                              /* Locale fallbacks — non-Latin scripts (Noto CJK/Cyrillic/Greek, Vazirmatn Arabic). */
                                              --font-noto-sans: ${notoSans.style
                                                ?.fontFamily};
                                              --font-noto-serif: ${notoSerif.style
                                                ?.fontFamily};
                                              --font-vazirmatn: ${vazirmatn.style
                                                ?.fontFamily};
                                              /* Translator message text — the standalone heidiTranslator stack; never repointed by the body-font refresh (ANZ-2440). */
                                              --font-translator-transcript: ${translatorTranscriptFontFamily};
                                              /* Signature faces — handwritten fonts for the Defaults footer signature editor. */
                                              --font-dancing-script: ${dancingScript.style
                                                ?.fontFamily};
                                              --font-great-vibes: ${greatVibes.style
                                                ?.fontFamily};
                                              --font-allura: ${allura.style?.fontFamily};
                                              --font-pacifico: ${pacifico.style
                                                ?.fontFamily};
                                              --font-satisfy: ${satisfy.style
                                                ?.fontFamily};
                                            }
                                          `}</style>
                                          <AbilityContext.Provider value={ability}>
                                            <WhiteLabelProvider>
                                              <AuthWrapper>
                                                <PatientDirectoryAnalyticsSyncMount />
                                                {isTauri() && (
                                                  <>
                                                    <SelfHealingStopEventsMount />
                                                    <RecordingLeaseMount />
                                                  </>
                                                )}
                                                {/* Plug-in confirmation for every page, including those that
                                                    opt out of global modals. */}
                                                {isTauri() && (
                                                  <DictaphoneConnectedToastMount />
                                                )}
                                                {/* Transactional, user-triggered gate: keep one
                                                    app-root host even when launch modals are hidden. */}
                                                <TemplateQualityGateModal />
                                                {shouldMountGlobalModals && (
                                                  <>
                                                    <GlobalModalsMount />
                                                    {/* Rides with the modals: a page that opts out of them
                                                        skips the team-join check, and the note waits in
                                                        session storage for a page that mounts it. */}
                                                    <PendingTeamJoinGate
                                                      ports={pendingTeamJoinPorts}
                                                    />
                                                  </>
                                                )}
                                                <RpaRecorderRootEffects />
                                                <PictureInPictureProvider>
                                                  <OpenFeatureContextSync />
                                                  <OnPremFlagBundleSync />
                                                  <EvidenceCapabilitiesSync />
                                                  <ClientSdkSelfReportSync />
                                                  <ExternalLinkHandlerSync />
                                                  <InstallSourceContextSync />
                                                  <ApplicationInstallSync />
                                                  <SafeTeamMembershipSync />
                                                  <NavigationGuard />
                                                  <Migrations />
                                                  <UserThemeManager />
                                                  <TierColorThemeManager />
                                                  <TranslationsProvider />
                                                  <DeferredAppShellWidgets />
                                                  <ChronicleStateProvider>
                                                    {isTauri() && <TauriAppHeader />}
                                                    {/* Applies the page's
                                                        `layout` static. Renders
                                                        the page untouched when
                                                        there isn't one, which is
                                                        every page today. */}
                                                    <PageLayoutSlot
                                                      configuration={
                                                        Component.layoutConfiguration
                                                      }
                                                    >
                                                      <Component {...pageProps} />
                                                    </PageLayoutSlot>
                                                    <DeferredChronicleWidgets
                                                      isPyneEnabled={Boolean(
                                                        isPyneEnabled
                                                      )}
                                                    />
                                                    {IS_HEIDI_VERIFY_AUTO_MODE_ENABLED() && (
                                                      <>
                                                        <AutoVerifyAutoEnable />
                                                        <AutoVerifyCrossSessionWatcher />
                                                      </>
                                                    )}
                                                    {router.pathname !==
                                                      '/get-mobile' && <Toaster />}
                                                    {isNonProductionEnvironment &&
                                                      !process.env
                                                        .NEXT_PUBLIC_DISABLE_TANSTACK_DEVTOOLS && (
                                                        <ReactQueryDevtools />
                                                      )}
                                                  </ChronicleStateProvider>
                                                </PictureInPictureProvider>
                                              </AuthWrapper>
                                            </WhiteLabelProvider>
                                          </AbilityContext.Provider>
                                        </main>
                                      </LazyMotion>
                                    </SessionRecordingProvider>
                                  </HotkeysProvider>
                                </DialogDrawerRegistryProvider>
                              </DesignSystemProvider>
                            </TooltipProvider>
                          </DictationProvider>
                        </AppDirectionProvider>
                      </KindeBrowserClientProvider>
                    </ErrorBoundary>
                  </HeidiIntlProvider>
                </AppIntlErrorShell>
              </PersistQueryClientProvider>
            </PosthogProvider>
          </OpenFeatureProvider>
        </Provider>
      </PersistGate>
    </ThemeProvider>
  );
};

export default MyApp;
